<template>
  <default>
    <div class="tw-w-full">
      <bread-crumb :breadcrumbs="breadcrumbs" />

      <header class="renewals-header tw-mb-6">
        <div>
          <h2 class="renewals-title">Motor Renewals</h2>
          <p class="renewals-subtitle">Your policies coming up for renewal, soonest first.</p>
        </div>
      </header>

      <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
        <v-progress-circular :size="56" color="#00AEEF" indeterminate />
      </div>

      <template v-else-if="reminders.length">
        <div class="renewals-summary tw-mb-6">
          <div class="renewals-summary__lead" :class="{ 'renewals-summary__lead--alert': needsAttention }">
            <i class="ph renewals-summary__icon" :class="needsAttention ? 'ph-warning-circle' : 'ph-calendar-check'"></i>
            <div>
              <p class="renewals-summary__title">{{ headline.title }}</p>
              <p class="renewals-summary__body">{{ headline.body }}</p>
            </div>
          </div>
          <div class="renewals-summary__stats">
            <div v-for="stat in stats" :key="stat.label" class="renewals-stat">
              <span class="renewals-stat__value">{{ stat.value }}</span>
              <span class="renewals-stat__label">{{ stat.label }}</span>
            </div>
          </div>
        </div>

        <div class="renewals-list">
          <section v-for="section in sections" :key="section.key" class="renewals-section">
            <h3 class="renewals-section__title" :class="{ 'renewals-section__title--ended': section.ended }">
              {{ section.title }}
              <span class="renewals-section__count">{{ section.count }}</span>
            </h3>

            <div v-for="week in section.weeks" :key="week.key" class="renewals-week">
              <p v-if="week.title" class="renewals-week__title">
                {{ week.title }}
                <span class="renewals-week__range">{{ week.range }}</span>
              </p>

              <v-card
                v-for="item in week.items"
                :key="item.key"
                flat
                class="renewal-card"
              >
                <div class="renewal-card__body">
                  <div class="renewal-card__hero">
                    <div class="renewal-card__icon">
                      <v-icon size="32" color="#184679">mdi-car-outline</v-icon>
                    </div>
                    <div class="renewal-card__intro">
                      <h3 class="renewal-card__title">{{ item.title }}</h3>
                      <p class="renewal-card__policy-no">{{ item.policyNo }}</p>
                    </div>
                    <div class="renewal-card__due">
                      <span class="renewal-chip" :class="`renewal-chip--${item.urgency.key}`">
                        {{ item.urgency.label }}
                      </span>
                      <span class="renewal-card__due-text">{{ item.dueText }}</span>
                    </div>
                  </div>

                  <p class="renewal-card__nudge">{{ item.nudge }}</p>

                  <div class="renewal-card__details">
                    <div class="renewal-detail">
                      <span class="renewal-detail__label">Product</span>
                      <span class="renewal-detail__value">{{ item.raw.product || 'Motor' }}</span>
                    </div>
                    <div class="renewal-detail">
                      <span class="renewal-detail__label">Renewal date</span>
                      <span class="renewal-detail__value">{{ formatDate(item.raw.renewalDate) }}</span>
                    </div>
                    <div class="renewal-detail">
                      <span class="renewal-detail__label">Premium</span>
                      <span class="renewal-detail__value renewal-detail__value--premium">
                        KES {{ Number(item.raw.futurePremium || item.raw.premium || 0).toLocaleString() }}
                      </span>
                    </div>
                    <div class="renewal-detail">
                      <span class="renewal-detail__label">Sum insured</span>
                      <span class="renewal-detail__value">
                        KES {{ Number(item.raw.sumInsured || 0).toLocaleString() }}
                      </span>
                    </div>
                  </div>

                  <div class="renewal-card__actions">
                    <v-btn
                      v-if="item.renewable && batchNoOf(item)"
                      color="primary"
                      depressed
                      small
                      @click="renew(item)"
                    >
                      <v-icon left small>mdi-autorenew</v-icon>
                      Renew Now
                    </v-btn>
                    <v-btn
                      v-if="item.policyNo"
                      outlined
                      color="primary"
                      small
                      :to="{ name: 'SinglePolicy', params: { policyNo: item.policyNo } }"
                    >
                      View Policy
                    </v-btn>
                    <v-btn
                      v-if="!item.renewable"
                      outlined
                      color="primary"
                      small
                      :to="{ name: 'CustomerProductSelect' }"
                    >
                      Get a new quote
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </div>
          </section>
        </div>
      </template>

      <div v-else class="renewals-empty">
        <v-icon size="60" color="#CBD5E1">mdi-calendar-check-outline</v-icon>
        <p class="renewals-empty__title">You are all caught up</p>
        <p class="renewals-empty__subtitle">
          None of your motor policies are due for renewal in the next 90 days. We will remind you here when one is.
        </p>
      </div>
    </div>
  </default>
</template>

<script>
import Default from '@/components/layout/Default';
import BreadCrumb from '@/components/dashboard/BreadCrumb';
import { mapGetters } from 'vuex';
import { customerRenewalLocation, formatRenewalDate } from '@/utils/agentRenewal';
import { reminderHeadline } from '@/utils/renewalReminders';

export default {
  name: 'CustomerRenewals',
  components: { Default, BreadCrumb },
  data() {
    return {
      breadcrumbs: [
        { text: 'My Policies', disabled: false, to: { name: 'MyPolicies' } },
        { text: 'Renewals', disabled: true, to: { name: 'CustomerRenewals' } },
      ],
      loading: true,
    };
  },
  computed: {
    ...mapGetters('renewalReminders', ['reminders', 'groups', 'summary']),
    headline() {
      if (!this.summary.total) {
        return {
          title: 'Nothing to renew right now',
          body: 'The policies below ended more than 2 months ago. Get a new quote to insure them again.',
        };
      }
      return reminderHeadline(this.summary);
    },
    needsAttention() {
      return this.summary.ended + this.summary.urgent > 0;
    },
    stats() {
      const stats = [
        { label: 'Within 7 days', value: this.summary.urgent },
        { label: 'In 8 to 30 days', value: this.summary.soon },
        { label: 'Later', value: this.summary.later },
      ];
      if (this.summary.ended) stats.unshift({ label: 'Cover ended', value: this.summary.ended });
      return stats;
    },
    sections() {
      const { ended, months, undated } = this.groups;
      const sections = [];
      if (ended.length) {
        sections.push({
          key: 'ended',
          title: 'Cover has ended',
          ended: true,
          count: ended.length,
          weeks: [{ key: 'ended-all', items: ended }],
        });
      }
      months.forEach((m) => sections.push({
        key: m.key,
        title: m.isCurrent ? `${m.title} · this month` : m.title,
        count: m.count,
        weeks: m.weeks,
      }));
      if (undated.length) {
        sections.push({
          key: 'undated',
          title: 'Date to be confirmed',
          count: undated.length,
          weeks: [{ key: 'undated-all', items: undated }],
        });
      }
      return sections;
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('renewalReminders/refresh', { force: true });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    batchNoOf(item) {
      return item.raw.effectiveBatchNo || item.raw.batchNo;
    },
    formatDate(val) {
      return formatRenewalDate(val);
    },
    renew(item) {
      this.$router.push(customerRenewalLocation(item.raw, item.days)).catch(() => {});
    },
  },
  metaInfo: { title: 'Motor Renewals' },
};
</script>

<style scoped>
.renewals-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.renewals-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px;
}

.renewals-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

.renewals-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: stretch;
}

.renewals-summary__lead {
  flex: 1 1 320px;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #F0F7FF;
  color: #1E4276;
}

.renewals-summary__lead--alert {
  background: #FFF5F5;
  color: #9B2C2C;
}

.renewals-summary__icon {
  font-size: 26px;
  flex-shrink: 0;
}

.renewals-summary__title {
  margin: 0;
  font-weight: 800;
  font-size: 0.95rem;
}

.renewals-summary__body {
  margin: 2px 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
}

.renewals-summary__stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.renewals-stat {
  min-width: 104px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #E2E8F0;
}

.renewals-stat__value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1E4276;
}

.renewals-stat__label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94A3B8;
}

.renewals-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.renewals-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1E4276;
}

.renewals-section__title--ended {
  color: #C53030;
}

.renewals-section__count {
  padding: 1px 8px;
  border-radius: 999px;
  background: #E2E8F0;
  color: #475569;
  font-size: 0.72rem;
  letter-spacing: 0;
}

.renewals-week {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
}

.renewals-week__title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
}

.renewals-week__range {
  margin-left: 6px;
  font-weight: 500;
  color: #94A3B8;
}

.renewal-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(24, 70, 121, 0.06) !important;
}

.renewal-card__body {
  padding: 1.25rem;
}

@media (min-width: 768px) {
  .renewal-card__body {
    padding: 1.5rem 1.75rem;
  }
}

.renewal-card__hero {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 0.5rem;
}

.renewal-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #E8EEF7 0%, #F4F7FB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.renewal-card__intro {
  flex: 1;
  min-width: 0;
}

.renewal-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #1E4276;
}

.renewal-card__policy-no {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #64748B;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.renewal-card__due {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.renewal-card__due-text {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1E4276;
}

.renewal-chip {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
}

.renewal-chip--ended { background: #FED7D7; color: #9B2C2C; }
.renewal-chip--now { background: #FEEBC8; color: #9C4221; }
.renewal-chip--soon { background: #E0F2FE; color: #075985; }
.renewal-chip--upcoming,
.renewal-chip--ready { background: #E8EEF7; color: #1E4276; }
.renewal-chip--closed { background: #F1F5F9; color: #64748B; }

.renewal-card__nudge {
  margin: 0 0 1rem 62px;
  font-size: 0.85rem;
  color: #475569;
}

.renewal-card__details {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  padding: 14px 16px;
  background: #F8FAFC;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.renewal-detail {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.renewal-detail__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94A3B8;
}

.renewal-detail__value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1E4276;
}

.renewal-detail__value--premium {
  color: #184679;
  font-size: 1rem;
}

.renewal-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.renewals-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;
}

.renewals-empty__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.renewals-empty__subtitle {
  max-width: 420px;
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

@media (max-width: 600px) {
  .renewals-title {
    font-size: 1.2rem;
  }

  .renewals-summary__stats {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 8px;
  }

  .renewals-stat {
    min-width: 0;
    padding: 10px;
  }

  .renewal-card__body {
    padding: 1rem;
  }

  .renewal-card__hero {
    flex-wrap: wrap;
    gap: 10px 12px;
  }

  .renewal-card__icon {
    width: 40px;
    height: 40px;
  }

  .renewal-card__due {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .renewal-card__nudge {
    margin-left: 0;
    font-size: 0.82rem;
  }

  .renewal-card__details {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 12px;
  }

  .renewal-detail__value {
    font-size: 0.85rem;
  }

  .renewal-card__actions .v-btn {
    flex: 1 1 auto;
  }
}
</style>
